import { makeAutoObservable, runInAction, flow } from "mobx";
import { clusterApiUrl, Connection, PublicKey, Transaction, VersionedTransaction } from "@solana/web3.js";
import { Keyring, Platform, SolanaNetwork, WalletConfig } from "../Types/ExtensionTypes.ts";
import { AnchorProvider, Program } from "@coral-xyz/anchor";
import { getKeypairFromPublicKeyAndPassphrase } from "../Functions/General.ts";
import { CoolProgram } from "../../../backend/target/types/cool_program.ts";
import { idl_json, SOLANA_CLUSTERS_RPC } from "../../Constants.ts";
import { get_object, set_object } from "../Functions/StorageFunctions.ts";
import { RootStore } from "./RootStore.ts";

export type Passphrase = string | undefined;

interface Signer {
    publicKey: PublicKey;
    secretKey: Uint8Array;
}

// const BLCH_ENDPOINT: string = "https://sol.blch.ru/rpc/";
const BLCH_ENDPOINT = process.env.MAINNET_RPC as string;

const CUSTOMRPC = SOLANA_CLUSTERS_RPC.LOCAL_NET;

export class GlobalStore {
    rootStore: RootStore;
    keyring: Keyring | undefined;
    provider: AnchorProvider | null = null;
    passphrase: Passphrase = undefined;
    program: Program<CoolProgram> | null = null;
    isOnline: boolean = navigator.onLine;

    // network: Cluster | 'localnet' = 'devnet'; // default to Devnet
    network: SolanaNetwork = "mainnet-beta"; // default to Mainnet
    connection: Connection | undefined;
    connectionStatus: "pending" | "connected" | "failed" = "connected"; // New state
    connectionError: string | null = null; // New state for error message

    getConnection() {
        if (!this.connection) throw new Error("Connection is undefined!");
        return this.connection;
    }

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this);
        // this.connection = new Connection(this.network === 'localnet' ? this.network : clusterApiUrl(this.network));
        // this.connection = new Connection(
        //     this.network === 'localnet'
        //         ? SOLANA_CLUSTERS_RPC.LOCAL_NET
        //         : this.network === 'mainnet-beta'
        //           ? BLCH_ENDPOINT
        //           : clusterApiUrl(this.network),
        //     'confirmed'
        // );
    }

    async setNetwork(newNetwork: SolanaNetwork) {
        console.log("setNetwork", newNetwork);
        if (this.connection !== undefined && this.network === newNetwork) return; // Avoid redundant checks
        runInAction(() => {
            this.network = newNetwork;
            this.connectionStatus = "pending";
            this.connectionError = null;
        });

        // Create new connection
        const newConnection = new Connection(
            newNetwork === "localnet"
                ? SOLANA_CLUSTERS_RPC.LOCAL_NET
                : newNetwork === "mainnet-beta"
                  ? BLCH_ENDPOINT
                  : // TODO: add logic for custom RPC
                    newNetwork === "customRPC"
                    ? CUSTOMRPC
                    : clusterApiUrl(newNetwork),
            {
                commitment: "confirmed",
            }
        );

        try {
            // Test the connection by fetching the latest block height
            await newConnection.getSlot("confirmed");
        } catch (error: any) {
            console.error(error);
            // If failed, keep the old connection and set error
            runInAction(() => {
                this.connectionStatus = "failed";
                this.connectionError = `Failed to connect to ${newNetwork}: ${error.message}`;
            });
            return;
        }

        if (this.connection) {
            this.rootStore.WalletStore.cleanupSubscriptions(this.connection);
        }
        this.rootStore.WalletStore.clearBalances();

        // If successful, update the connection and status
        runInAction(() => {
            this.connection = newConnection;
            this.connectionStatus = "connected";

            if (this.passphrase && this.provider?.wallet) {
                this.provider = new AnchorProvider(
                    newConnection,
                    this.provider.wallet, // Reuse existing wallet
                    { preflightCommitment: "processed" }
                );
                this.program = new Program<CoolProgram>(idl_json, this.provider);
            }
        });

        if (this.passphrase) {
            await this.rootStore.WalletStore.startFetchingBalances();
        }
    }

    setIsOnline(isOnline: boolean) {
        this.isOnline = isOnline;
    }

    set_keyring = flow(function* (this: GlobalStore, keyring: Keyring | undefined) {
        if (keyring) yield set_object("keyring", keyring);
        this.keyring = keyring;
    });

    getProvider(): AnchorProvider {
        if (!this.provider) {
            throw new Error("Anchor Provider is undefined!");
        }
        return this.provider;
    }

    /**
     * Fetches either public_keys or active_key from a given platform in the store's keyring.
     * @param platform The platform to fetch from (e.g., 'solana', 'ethereum').
     * @param key The key to fetch ('public_keys' or 'active_key').
     * @returns The requested value or undefined if not found.
     */
    fetchPlatformData<K extends keyof NonNullable<Keyring["platforms"][Platform]>>(
        platform: Platform,
        key: K
    ): NonNullable<Keyring["platforms"][Platform]>[K] {
        if (!this.keyring) {
            throw new Error("Keyring is not defined");
        }

        const platformData = this.keyring.platforms?.[platform];
        if (!platformData) {
            throw new Error(`Platform data not found for platform: ${platform}`);
        }

        const value = platformData[key];
        if (value === undefined) {
            throw new Error(`Key "${key}" not found for platform: ${platform}`);
        }

        return value;
    }

    async set_active_account(address: string) {
        this.rootStore.WalletStore.clearBalances();
        const keyring = await get_object("keyring");
        if (keyring === null) {
            throw new Error("No Keyring in store");
        }
        const active_platform = keyring.active_platform;
        keyring.platforms[active_platform]!.active_key = address;

        await this.set_keyring(keyring);
        await this.set_passphrase(this.passphrase);
    }

    async set_passphrase(passphrase: Passphrase) {
        this.passphrase = passphrase;
        if (passphrase === undefined) {
            runInAction(() => {
                this.provider = null;
                this.program = null;
            });
            return;
        }
        const keypair = await getKeypairFromPublicKeyAndPassphrase(
            new PublicKey(this.fetchPlatformData("solana", "active_key")),
            passphrase
        );
        console.log("keypair");
        console.log(keypair);

        if (keypair === undefined) {
            throw new Error("No Keypair found!");
        }

        const signer: Signer = {
            publicKey: new PublicKey(keypair.publicKey),
            secretKey: keypair.secretKey,
        };
        const wallet = {
            publicKey: new PublicKey(keypair.publicKey),
            async signTransaction<T extends Transaction | VersionedTransaction>(tx: T): Promise<T> {
                console.log("tx");
                console.log(tx);
                if (tx instanceof Transaction) {
                    tx.partialSign(signer);
                    return tx;
                } else if (tx instanceof VersionedTransaction) {
                    tx.sign([signer]);
                    return tx;
                }
                throw new Error("Unsupported transaction type");
            },
            async signAllTransactions<T extends Transaction | VersionedTransaction>(txs: T[]): Promise<T[]> {
                // txs.forEach((tx) => tx.sign([signer])); // Fully sign each transaction
                // return txs;
                return txs.map((t) => {
                    if (t instanceof VersionedTransaction) {
                        t.sign([signer]);
                    } else if (t instanceof Transaction) {
                        t.partialSign(signer);
                    }
                    return t;
                });
            },
        };

        runInAction(() => {
            this.provider = new AnchorProvider(this.getConnection(), wallet, {
                preflightCommitment: "processed",
            });
            this.program = new Program<CoolProgram>(idl_json, this.provider);
        });
    }

    show_welcome_page = true;

    set_show_welcome_page(show_welcome_page: boolean) {
        this.show_welcome_page = show_welcome_page;
    }

    is_locked = true;

    set_is_locked(is_locked: boolean) {
        this.is_locked = is_locked;
    }

    walletConfig: WalletConfig | undefined = undefined;

    setWalletConfig(walletConfig: WalletConfig) {
        this.walletConfig = walletConfig;
    }

    async loadWalletConfig(): Promise<WalletConfig> {
        const wallet_config = await get_object("walletConfig");
        if (!wallet_config) throw new Error("no wallet config in localStorage");
        this.setWalletConfig(wallet_config);
        return wallet_config;
    }

    getWalletConfig(): WalletConfig {
        if (!this.walletConfig) throw new Error("No wallet config in GlobalStore");
        return this.walletConfig;
    }
}
